"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useBranchesEnabled } from "@/hooks/api/useBranchesEnabled";
import { api } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Branch = { id: number; name: string; address?: string | null; phone?: string | null };

export default function AdminBranchesPage() {
  const qc = useQueryClient();
  const { branchesEnabled } = useBranchesEnabled();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "branches"],
    queryFn: async () => {
      const { data: d } = await api.get<{ branches: Branch[] }>("/api/branches");
      return d.branches;
    },
  });

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");

  const create = useMutation({
    mutationFn: async () => {
      await api.post("/api/branches", { name, address, phone });
    },
    onSuccess: async () => {
      setName("");
      setAddress("");
      setPhone("");
      await qc.invalidateQueries({ queryKey: ["admin", "branches"] });
    },
  });

  const [fromB, setFromB] = useState("");
  const [toB, setToB] = useState("");
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState("");
  const [notes, setNotes] = useState("");

  const transfer = useMutation({
    mutationFn: async () => {
      const { data: created } = await api.post<{ transfer: { id: number } }>("/api/branch-stock-transfers", {
        from_branch_id: Number(fromB),
        to_branch_id: Number(toB),
        notes,
        items: [{ product_id: Number(productId), qty: Number(qty) }],
      });
      const id = created.transfer.id;
      await api.post(`/api/branch-stock-transfers/${id}/dispatch`, {});
      return id;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin", "branches"] });
    },
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (isError) {
    return (
      <p className="text-sm text-destructive">
        {(error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Failed to load branches."}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Branches</h1>
        <p className="text-sm text-muted-foreground">List branches and dispatch stock transfer (draft → dispatch).</p>
      </div>

      {branchesEnabled ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">New branch</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 max-w-md">
            <div className="grid gap-2">
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>Address</Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <Button type="button" disabled={create.isPending || !name} onClick={() => create.mutate()}>
              Create branch
            </Button>
          </CardContent>
        </Card>
      ) : (
        <p className="text-sm text-muted-foreground max-w-lg">
          Multi-branch is off in Settings. Turn it on to add locations or run stock transfers between branches.
        </p>
      )}

      {branchesEnabled ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Stock transfer (dispatch now)</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 max-w-md">
            <div className="grid gap-2">
              <Label>From branch id</Label>
              <Input value={fromB} onChange={(e) => setFromB(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>To branch id</Label>
              <Input value={toB} onChange={(e) => setToB(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>Source product id</Label>
              <Input value={productId} onChange={(e) => setProductId(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>Qty</Label>
              <Input value={qty} onChange={(e) => setQty(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>Notes</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <Button
              type="button"
              variant="secondary"
              disabled={transfer.isPending || !fromB || !toB || !productId || !qty}
              onClick={() => transfer.mutate()}
            >
              Create & dispatch
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Branches</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 pr-4">ID</th>
                <th className="py-2 pr-4">Name</th>
                <th className="py-2">Phone</th>
              </tr>
            </thead>
            <tbody>
              {(data ?? []).map((b) => (
                <tr key={b.id} className="border-b border-border/60">
                  <td className="py-2 pr-4">{b.id}</td>
                  <td className="py-2 pr-4">{b.name}</td>
                  <td className="py-2">{b.phone ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
